import { faClone, faCog, faEye, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/Button';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { Icon } from 'src/components/Icon';
import { SearchParamsPagination } from 'src/components/Pagination';
import { SearchParamSearchTextField } from 'src/components/SearchTextField';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { StickyCard } from 'src/components/StickyCard';
import { DataTable } from 'src/components/Table/DataTable';
import { Theme } from 'src/components/muiTheme/types';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { Assessment } from 'src/models/assessment/_types';
import {
  cloneAssessment,
  deleteAssessment,
  fetchAssessments,
  getAssessmentAutomationCount,
} from 'src/models/assessment/assessment';
import { TrainerCell } from 'src/shared/TableCells/TrainerCell';
import { usePermissionCheck } from 'src/shared/usePermissionCheck';
import api from 'src/utils/ajax';
import { formatMonthDayYear } from 'src/utils/dateFormatter';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';


export const AssessmentsIndexPage = () => {
  const { t: tAssessment } = useTranslation('translation', {
    keyPrefix: 'assessment',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canViewAssessment, canNotEditYourAssessment, sessionUser } =
    usePermissionCheck();
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const { params, updateParam } = useListSearchParams({
    defaultPerPage: 10,
  });

  const [confirmDeleteProps, confirm] = useConfirmDialogComponent();

  // Fetch assessments
  const { data, isLoading } = useQuery({
    queryKey: ['assessments', params.page, params.search, params.perPage],
    queryFn: () =>
      fetchAssessments({
        page: params?.page || 1,
        perPage: params?.perPage,
        search: params?.search,
      }),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteAssessment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['assessments'],
      });
      toast.success(tAssessment('deleteSuccessMessage'));
    },
    onError: (error: any) => {
      toast.error(api.formatError(error));
    },
  });

  // Clone mutation
  const cloneMutation = useMutation({
    mutationFn: (assessment: Assessment) =>
      cloneAssessment({
        assessmentId: assessment.id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['assessments'],
      });
      toast.success(tAssessment('cloneSuccessMessage'));
    },
    onError: (error: any) => {
      toast.error(api.formatError(error));
    },
  });

  // Handle delete with automation count check
  const handleDelete = async (assessment: Assessment) => {
    try {
      const automationCount = await getAssessmentAutomationCount({
        assessmentId: assessment.id,
      });

      const baseMessage = tAssessment('deleteAssessmentConfirm');

      const automationMessage =
        automationCount > 0
          ? tAssessment('deleteAssessmentConfirmAutomationMessage', {
              count: automationCount,
            })
          : '';

      if (
        await confirm({
          title: tAssessment('deleteAssessment'),
          message: baseMessage + automationMessage,
        })
      ) {
        deleteMutation.mutate({ assessmentId: assessment.id });
      }
    } catch (error) {
      toast.error(api.formatError(error));
    }
  };

  // Handle clone
  const handleClone = async (assessment: Assessment) => {
    if (
      await confirm({
        title: tCommon('confirm'),
        message: tAssessment('cloneConfirm'),
      })
    ) {
      cloneMutation.mutate(assessment);
    }
  };

  // Check if user can edit assessment
  const canEditAssessment = (assessment: Assessment) => {
    return !(
      canNotEditYourAssessment && assessment.subTrainerId !== sessionUser.id
    );
  };

  const columns = [
    {
      id: 'name',
      label: tCommon('labels.name'),
    },
    {
      id: 'createdAt',
      label: tCommon('labels.created'),
      format: (value: number) => formatMonthDayYear(value),
    },
    {
      id: 'creator',
      label: tCommon('labels.creator'),
      format: (_: any, row: Assessment) => (
        <TrainerCell id={row.subTrainerId} />
      ),
      show: canViewAssessment,
    },
  ].filter((column) => column.show !== false);

  // Table actions
  const actions = [
    {
      icon: faEye,
      label: tCommon('actions.preview'),
      onClick: (assessment: Assessment) =>
        navigate(`/trainer/assessment/${assessment.id}`),
    },
    {
      icon: faCog,
      label: tCommon('actions.edit'),
      onClick: (assessment: Assessment) =>
        navigate(`/trainer/assessments/${assessment.id}`),
      showAction: canEditAssessment,
    },
    {
      icon: faClone,
      label: tCommon('actions.clone'),
      onClick: handleClone,
    },
    {
      icon: faTrash,
      label: tCommon('actions.delete'),
      onClick: handleDelete,
      showAction: canEditAssessment,
    },
  ];

  useEffect(() => {
    if (!canViewAssessment) {
      navigate('/dashboard');
    }
  }, [canViewAssessment, navigate]);

  return (
    <>
      <StickyCard
        title={tAssessment('plural')}
        contentSx={{ p: 0 }}
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<Icon icon={faPlus} />}
            onClick={() => navigate('/trainer/assessments/new')}
          >
            {smDown ? tCommon('actions.new') : tAssessment('addNewAssessment')}
          </Button>
        }
      >
        <SectionToolbar>
          <SearchParamSearchTextField
            params={params}
            updateParam={updateParam}
            sx={{ width: smDown ? '100%' : '280px' }}
            placeholder={tAssessment('searchAssessments')}
          />
        </SectionToolbar>

        <DataTable
          columns={columns}
          rows={data?.assessments || []}
          noResultsText={tAssessment('noAssessments')}
          loading={isLoading}
          rowActions={actions}
          onClickRow={(row) => navigate(`/trainer/assessments/${row.id}`)}
          fixedActions
        />

        {/* Pagination */}
        <SearchParamsPagination
          params={params}
          updateParam={updateParam}
          total={data?.total || 0}
          itemsPerPage={params.perPage}
        />
      </StickyCard>
      {/* Confirm dialog */}
      <ConfirmDialog {...confirmDeleteProps} />
    </>
  );
};
